import { useToast, type Toast } from '@/hooks/useToast';

const TYPE_STYLES: Record<string, { bg: string; border: string; text: string; icon: string }> = {
  success: { bg: 'bg-success-light', border: 'border-[#40916C]', text: 'text-primary-dark', icon: '\u2713' },
  error: { bg: 'bg-danger-light', border: 'border-[#BC4749]', text: 'text-danger', icon: '\u2717' },
  warning: { bg: 'bg-warning-light', border: 'border-[#C2410C]', text: 'text-warning', icon: '\u26A0' },
  info: { bg: 'bg-[#DBEAFE]', border: 'border-[#1D4ED8]', text: 'text-info', icon: '\u2139' },
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const styles = TYPE_STYLES[toast.type] || TYPE_STYLES.info;
  return (
    <div
      role="alert"
      className={`flex items-start gap-2 px-4 py-3 rounded-[14px] border shadow-md ${styles.bg} ${styles.border} ${styles.text} animate-in slide-in-from-top-2`}
    >
      <span className="text-body font-bold flex-shrink-0">{styles.icon}</span>
      <p className="text-body-sm font-medium flex-1">{toast.message}</p>
      {toast.action && (
        <button
          type="button"
          onClick={() => { toast.action!.onClick(); onDismiss(); }}
          className={`text-body-sm font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:rounded ${styles.text}`}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        className="text-body opacity-60 hover:opacity-100 flex-shrink-0"
        aria-label="Dismiss"
      >
        &times;
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 w-full max-w-sm px-4 md:px-0">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} />
      ))}
    </div>
  );
}
